// src/components/navbar/DesktopMenu.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import AvatarMenu from "./AvatarMenu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { getOfficialDocsConfig } from "../../services/officialDocsService";
import { getAppMovilConfig } from "../../services/appMovilConfigService";

export default function DesktopMenu({
  menuItems,
  user,
  location,
  navigate,
  onLogout,
  userDisplayName,
  userAvatarUrl,
}) {
  const leftItems = useMemo(
    () =>
      (menuItems || []).filter(
        (i) =>
          i &&
          String(i.text || "").toLowerCase() !== "mi perfil" &&
          String(i.path || "") !== "/profile" &&
          String(i.text || "").toUpperCase() !== "ÁREA DE TRABAJO"
      ),
    [menuItems]
  );

  const isAdmin = user?.role === "admin";

  // ===== Config "Documentación" (backend) =====
  const [docsCfg, setDocsCfg] = useState(null);
  const [docsLoading, setDocsLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const cfg = await getOfficialDocsConfig();
        if (active) setDocsCfg(cfg);
      } catch {
        if (active) setDocsCfg(null);
      } finally {
        if (active) setDocsLoading(false);
      }
    };
    load();
    const onCfgUpdated = () => load();
    window.addEventListener("official-docs-config-updated", onCfgUpdated);
    return () => {
      active = false;
      window.removeEventListener("official-docs-config-updated", onCfgUpdated);
    };
  }, []);

  // ===== Config "APP MÓVIL" (backend) =====
  const [appMovilEnabled, setAppMovilEnabled] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const cfg = await getAppMovilConfig();
        if (active) setAppMovilEnabled(cfg.enabled);
      } catch {
        if (active) setAppMovilEnabled(false);
      }
    };
    load();
    const onCfgUpdated = () => load();
    window.addEventListener("app-movil-config-updated", onCfgUpdated);
    return () => {
      active = false;
      window.removeEventListener("app-movil-config-updated", onCfgUpdated);
    };
  }, []);

  // Rutas base
  const DOC_LINKS = [
    { key: "showPublicAccount",     label: "Cuenta pública",                  path: "/annual-reports" },
    { key: "showEstatutos",         label: "Estatutos",                       path: "/statutes" },
    { key: "showCodigoEtica",       label: "Código de ética",                 path: "/ethics-code" },
    { key: "showModeloPrevencion",  label: "Modelo de prevención del delito", path: "/crime-prevention" },
    { key: "showReglamentoInterno", label: "Reglamento interno",              path: "/internal-regulation" },
    { key: "showProtocolos",        label: "Protocolos",                      path: "/protocols" },
    { key: "showMaterialInteres",   label: "Material de interés",             path: "/material-content" },
  ];

  // Grupos
  const OFFICIAL_KEYS = new Set([
    "showPublicAccount",
    "showEstatutos",
    "showCodigoEtica",
    "showModeloPrevencion",
    "showReglamentoInterno",
    "showProtocolos",
  ]);
  const officialBase = DOC_LINKS.filter(l => OFFICIAL_KEYS.has(l.key));
  const materialItem = DOC_LINKS.find(l => l.key === "showMaterialInteres");

  const visibleOfficialLinks = useMemo(() => {
    if (isAdmin) return officialBase;
    if (!docsCfg) return [];
    return officialBase.filter((l) => {
      if (l.key === "showPublicAccount") {
        return !!(docsCfg.showPublicAccount || docsCfg.showAnnualReports);
      }
      return !!docsCfg[l.key];
    });
  }, [isAdmin, docsCfg]);

  const showMaterialEntry =
    !!materialItem && (isAdmin || (!docsLoading && docsCfg?.showMaterialInteres));

  const showDocsMenu =
    isAdmin ||
    (!docsLoading &&
      docsCfg?.docsMenuEnabled &&
      (visibleOfficialLinks.length > 0 || showMaterialEntry));

  // ===== estado dropdowns =====
  const [openNE, setOpenNE] = useState(false);      // Noticias y eventos
  const [openDOC, setOpenDOC] = useState(false);    // DOCUMENTACIÓN (nivel 1)
  const [openDOCSub, setOpenDOCSub] = useState(false); // sub "Documentación oficial"

  const neWrapRef = useRef(null);
  const docWrapRef = useRef(null);

  const closeTimers = useRef({ ne: null, doc: null, docsub: null });
  const clearTimer = (key) => {
    if (closeTimers.current[key]) {
      clearTimeout(closeTimers.current[key]);
      closeTimers.current[key] = null;
    }
  };
  const openWithHover = (key) => {
    clearTimer(key);
    if (key === "ne") setOpenNE(true);
    if (key === "doc") setOpenDOC(true);
    if (key === "docsub") { setOpenDOC(true); setOpenDOCSub(true); }
  };
  const closeWithDelay = (key, ms = 260) => {  // Se aumentó el tiempo de espera
    clearTimer(key);
    closeTimers.current[key] = setTimeout(() => {
      if (key === "ne") setOpenNE(false);
      if (key === "doc") { setOpenDOC(false); setOpenDOCSub(false); }
      if (key === "docsub") setOpenDOCSub(false);
    }, ms);
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (neWrapRef.current?.contains(e.target) || docWrapRef.current?.contains(e.target)) return;
      setOpenNE(false);
      setOpenDOC(false);
      setOpenDOCSub(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const displayName =
    userDisplayName || user?.business_name || user?.name || "Usuario";
  const avatarUrl = userAvatarUrl || "";

  const go = (path) => () => {
    setOpenNE(false);
    setOpenDOC(false);
    setOpenDOCSub(false);
    navigate(path);
  };

  // ——— estilos tabs
  const tabClass = (active) =>
    `relative text-white text-xl font-medium flex items-center gap-1 cursor-pointer transition-all duration-300 whitespace-nowrap
     ${active ? "text-[#DC3465]" : "hover:text-[#48cae4]"}`;

  const MenuButton = ({ children, open }) => (
    <div className={tabClass(open)} style={{ padding: "8px 0" }}>
      {children}
      <ExpandMoreIcon
        sx={{ fontSize: 20, ml: "2px", transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .2s" }}
      />
    </div>
  );

  // DropPanel: Agregar overflow-visible
  const DropPanel = ({ children }) => (
    <div
      className="absolute top-full left-0 mt-2 rounded-xl shadow-[0_20px_40px_rgba(0,0,0,0.35)]"
      style={{ zIndex: 300, width: 300, fontFamily: "Nunito, sans-serif", overflow: "visible" }} 
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="h-1 w-full bg-[#0B3D66]" />
      <div className="bg-[#0B3D66] border-x border-[#0b4678]">
        <div className="p-2 relative">{children}</div>
      </div>
      <div className="h-[6px] bg-[#0B3D66] border-x border-b border-[#0b4678]" />
    </div>
  );

  const SubDropPanel = ({ children }) => (
    <div
      className="absolute top-0 left-full ml-2 rounded-xl overflow-hidden shadow-[0_20px_40px_rgba(0,0,0,0.35)]"
      style={{ zIndex: 310, width: 300, fontFamily: "Nunito, sans-serif" }}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseEnter={() => openWithHover("docsub")}
      onMouseLeave={() => closeWithDelay("docsub")}
    >
      <div className="h-1 w-full bg-[#0B3D66]" />
      <div className="bg-[#0B3D66] border-x border-[#0b4678]">
        <div className="p-2">{children}</div>
      </div>
      <div className="h-[6px] bg-[#0B3D66] border-x border-b border-[#0b4678]" />
    </div>
  );

  const DropItem = ({ label, onClick, active, hasSubmenu = false, onMouseEnter, onMouseLeave }) => (
    <button
      type="button"
      onClick={hasSubmenu ? undefined : onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`w-full text-left flex items-center justify-between gap-3 rounded-md transition-colors
        ${active ? "bg-[#0e4775]" : "hover:bg-[#163457]"} text-[#e9f1f7]`}
      style={{ padding: "12px 16px" }}
    >
      <span className="font-medium" style={{ fontSize: "1.06rem", lineHeight: 1.25, letterSpacing: "0.2px" }}>
        {label}
      </span>
      {hasSubmenu && <ChevronRightIcon sx={{ fontSize: 20, opacity: 0.9 }} />}
    </button>
  );

  return (
    <div className="ml-4 hidden md:flex flex-1 items-center justify-between overflow-visible">
      {/* IZQUIERDA */}
      <div className="grid grid-flow-col auto-cols-max items-center gap-x-[clamp(12px,1.6vw,26px)] whitespace-nowrap">

        {/* Noticias + Eventos */}
        <div
          className="relative"
          ref={neWrapRef}
          onMouseEnter={() => openWithHover("ne")}
          onMouseLeave={() => closeWithDelay("ne")}
        >
          <MenuButton open={openNE}>NOTICIAS Y EVENTOS</MenuButton>
          {openNE && (
            <DropPanel>
              <DropItem label="Noticias" active={location.pathname === "/news"} onClick={go("/news")} />
              <DropItem label="Calendario de Eventos" active={location.pathname === "/events"} onClick={go("/events")} />
            </DropPanel>
          )}
        </div>

        {/* Convenios */}
        <div
          className={tabClass(location.pathname === "/agreements")}
          onClick={() => navigate("/agreements")}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate("/agreements")}
        >
          CONVENIOS
        </div>

        {/* Socios */}
        <div
          className={tabClass(location.pathname === "/companies")}
          onClick={() => navigate("/companies")}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate("/companies")}
        >
          SOCIOS
        </div>

        {/* Vinculación */}
        <div
          className={tabClass(location.pathname === "/services")}
          onClick={() => navigate("/services")}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate("/services")}
        >
          VINCULACIÓN COMERCIAL
        </div>

        {/* App Móvil */}
        {(isAdmin || appMovilEnabled) && (
          <div
            className={tabClass(location.pathname === "/download-app")}
            onClick={() => navigate("/download-app")}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate("/download-app")}
          >
            APP MÓVIL
          </div>
        )}

        {/* DOCUMENTACIÓN */}
        {showDocsMenu && (
          <div
            className="relative"
            ref={docWrapRef}
            onMouseEnter={() => openWithHover("doc")}
            onMouseLeave={() => closeWithDelay("doc")}
          >
            <MenuButton open={openDOC}>DOCUMENTACIÓN</MenuButton>

            {openDOC && (
              <DropPanel>
                {/* 1) Material de interés (enlace directo) */}
                {showMaterialEntry && (
                  <DropItem
                    label={materialItem.label}
                    active={location.pathname === materialItem.path}
                    onClick={go(materialItem.path)}
                  />
                )}

                {/* 2) Documentación oficial (sub-cascada) */}
                {visibleOfficialLinks.length > 0 && (
                  <div className="relative mt-1">
                    <DropItem
                      label="Documentación oficial"
                      hasSubmenu
                      onMouseEnter={() => openWithHover("docsub")}
                      onMouseLeave={() => closeWithDelay("docsub")}
                    />
                    {openDOCSub && (
                      <SubDropPanel>
                        {visibleOfficialLinks.map((l) => (
                          <DropItem
                            key={l.key}
                            label={l.label}
                            active={location.pathname === l.path}
                            onClick={go(l.path)}
                          />
                        ))}
                      </SubDropPanel>
                    )}
                  </div>
                )}
              </DropPanel>
            )}
          </div>
        )}
      </div>

      {/* DERECHA – avatar (sin cambios) */}
      {user && (
        <div className="shrink-0">
          <AvatarMenu
            user={user}
            displayName={displayName}
            avatarUrl={avatarUrl}
            onProfile={() => navigate("/profile")}
            onAdmin={isAdmin ? () => navigate("/panel") : undefined}
            onLogout={onLogout}
          />
        </div>
      )}
    </div>
  );
}
